import { useMemo, useCallback } from 'react';

import { capitalize } from 'lodash';
import { useSelectedView } from 'reducers/viewsSlice';
import { convertToCSVOneOnOne, parseCSVOneOnOne } from 'screens/oneOnOneRabbit/Insights/features/AgGridTable/helper';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';

import { Download } from 'assets/assetsV1/iconComponents';

import { sendDownloadCSVEvent } from 'constants/mixpanelHelper';

type Props = {
  gridRef: any;
};

export const useOneOnOneEllipsisValues = ({ gridRef }: Props) => {
  const cohort = useSelectedView();
  const { groupByKeys } = useOneOnOneInsightsFiltersData();

  const oneOnOneInsightsCSVExport = useCallback(
    ({ exportedRows }: { exportedRows: 'all' | 'filteredAndSorted' }) => {
      sendDownloadCSVEvent({
        eventProperties: {
          source: '1:1s',
          type: 'insights',
          cohortName: cohort?.name,
          cohortId: cohort?.id,
          groupBy: groupByKeys?.displayName,
          splitBy: 'none',
        },
      });
      const data = gridRef?.current?.api.getDataAsCsv({
        fileName: 'one_on_one_insights_table.csv',
        allColumns: true,
        processCellCallback(params): string {
          const {
            value,
            column: { colId },
          } = params;

          if (colId === '1' || colId === 'ag-Grid-AutoColumn') {
            return value?.displayName;
          }
          // id for Topic Distribution
          if (colId === '9') {
            if (value !== undefined && value !== null) {
              const topicDistributionValues = Object.entries(value)
                .map(([category, value]) => `${capitalize(category)}: ${value}%`)
                .join(', ');

              return topicDistributionValues || 'NA';
            }
            return 'NA'; // Handle the case where value is undefined or null
          }
          return value ?? 'NA';
        },
        exportedRows,
      });

      const parsedCsv = parseCSVOneOnOne(data);

      // Filtering rows without "&" in the Hierarchy value
      const filteredData = parsedCsv.filter((row) => row['"Hierarchy"'].includes('&'));

      // Removing the Hierarchy column
      const dataToConvertIntoCsv = filteredData.map((row) => {
        delete row['"Hierarchy"'];
        delete row['""\r'];
        return row;
      });
      const csvData = convertToCSVOneOnOne(dataToConvertIntoCsv);

      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `one_on_one_insights_table ${Date.now()}.csv`;
      a.click();
    },
    [cohort?.id, cohort?.name, gridRef]
  );

  // eslint-disable-next-line arrow-body-style
  const options = useMemo(() => {
    return [
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => {
              oneOnOneInsightsCSVExport({
                exportedRows: 'filteredAndSorted',
              });
            },
            description: `Download selected metrics for filtered list of 1:1s along with basic details`,
          },
          {
            label: 'Detailed Download',
            onClick: () => {
              oneOnOneInsightsCSVExport({
                exportedRows: 'all',
              });
            },
            description: `Download all the metrics for all the 1:1s along with basic details`,
          },
        ],
      },
    ];
  }, [oneOnOneInsightsCSVExport]);

  return options;
};
